import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { dids } from '@/api/dids';
import type { DidCoverage } from '@/api/dids';
import { carriers } from '@/api/carriers';
import { resellers } from '@/api/resellers';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PhoneInput } from '@/components/PhoneInput';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}

const E164_RE = /^\+[1-9]\d{1,14}$/;

export function CreateDidDialog({ open, onOpenChange }: Props) {
  const qc = useQueryClient();
  const me = useCurrentUser();
  const isSuperAdmin = me.is_super_admin || me.role === 'super_admin';

  const [e164, setE164] = useState('');
  const [coverage, setCoverage] = useState<DidCoverage>('us');
  const [premium, setPremium] = useState(false);
  const [fsGateway, setFsGateway] = useState('');
  const [carrierId, setCarrierId] = useState('');
  const [resellerId, setResellerId] = useState(''); // super_admin only — assigns to a reseller's pool at creation
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Carrier list for the picker
  const { data: carrierList } = useQuery({
    queryKey: ['carriers', 'picker', 'voice'],
    queryFn: () => carriers.list({ limit: 200 }),
    enabled: open,
  });
  const voiceCarriers = (carrierList?.rows ?? []).filter(
    (c) => (c.kind === 'voice' || c.kind === 'both') && c.active,
  );

  // Reseller picker — super_admin only. Populates with all active resellers
  // so super_admin can drop the new DID into a reseller's pool right away.
  const { data: resellerList } = useQuery({
    queryKey: ['resellers', 'picker'],
    queryFn: () => resellers.list({ limit: 500 }),
    enabled: open && isSuperAdmin,
  });
  const resellerOptions = (resellerList?.rows ?? []).filter((r) => r.active);

  const create = useMutation({
    mutationFn: () =>
      dids.create({
        e164: e164.trim(),
        coverage,
        premium,
        fs_gateway_name: fsGateway.trim() || undefined,
        carrier_id: carrierId.trim() || undefined,
        reseller_id: isSuperAdmin && resellerId ? resellerId : undefined,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dids'] });
      onOpenChange(false);
      resetForm();
    },
    onError: (err: unknown) => {
      const code = (err as { code?: string }).code;
      if (code === 'did_already_exists') {
        setFieldErrors((prev) => ({ ...prev, e164: 'This number is already provisioned.' }));
      }
    },
  });

  function resetForm() {
    setE164('');
    setCoverage('us');
    setPremium(false);
    setFsGateway('');
    setCarrierId('');
    setResellerId('');
    setFieldErrors({});
  }

  function validate(): boolean {
    const errs: Record<string, string> = {};
    if (!e164.trim()) {
      errs.e164 = 'Phone number is required.';
    } else if (!E164_RE.test(e164.trim())) {
      errs.e164 = 'Enter a valid phone number.';
    }
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;
    create.mutate();
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!create.isPending) {
          onOpenChange(v);
          if (!v) resetForm();
        }
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add DID</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} noValidate className="space-y-4">
          {/* E.164 */}
          <div className="space-y-1.5">
            <Label htmlFor="did-e164">
              Phone number <span aria-hidden className="text-red-500">*</span>
            </Label>
            <PhoneInput
              id="did-e164"
              value={e164}
              onChange={(v) => {
                setE164(v);
                setFieldErrors((prev) => ({ ...prev, e164: '' }));
              }}
              resellerId={isSuperAdmin && resellerId ? resellerId : undefined}
              placeholder="212 555 1234"
              autoFocus
              aria-invalid={!!fieldErrors.e164}
            />
            {fieldErrors.e164 && (
              <p role="alert" className="text-xs text-red-500">{fieldErrors.e164}</p>
            )}
          </div>

          {/* Coverage */}
          <div className="space-y-1.5">
            <Label htmlFor="did-coverage">Coverage</Label>
            <Select value={coverage} onValueChange={(v) => setCoverage(v as DidCoverage)}>
              <SelectTrigger id="did-coverage">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="us">US</SelectItem>
                <SelectItem value="ca">CA</SelectItem>
                <SelectItem value="international">International</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Premium */}
          <div className="flex items-center gap-2">
            <Checkbox
              id="did-premium"
              checked={premium}
              onCheckedChange={(v) => setPremium(!!v)}
            />
            <Label htmlFor="did-premium" className="font-normal cursor-pointer">
              Premium number
            </Label>
          </div>

          {/* FreeSWITCH gateway (optional) */}
          <div className="space-y-1.5">
            <Label htmlFor="did-fs-gateway">FreeSWITCH gateway name <span className="text-muted-foreground text-xs">(optional)</span></Label>
            <Input
              id="did-fs-gateway"
              value={fsGateway}
              onChange={(e) => setFsGateway(e.target.value)}
              placeholder="e.g. bandwidth-main"
              autoComplete="off"
            />
          </div>

          {/* Reseller pool assignment (super_admin only) */}
          {isSuperAdmin && (
            <div className="space-y-1.5">
              <Label htmlFor="did-reseller">
                Assign to reseller pool <span className="text-muted-foreground text-xs">(optional)</span>
              </Label>
              <Select
                value={resellerId || '__unassigned__'}
                onValueChange={(v) => setResellerId(v === '__unassigned__' ? '' : v)}
              >
                <SelectTrigger id="did-reseller">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__unassigned__">Unassigned (platform pool)</SelectItem>
                  {resellerOptions.map((r) => (
                    <SelectItem key={r.id} value={r.id}>
                      {r.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Drop this number directly into a reseller's pool so their
                owner can later assign it to a customer. Leave unassigned
                to keep it in the platform pool.
              </p>
            </div>
          )}

          {/* Outbound carrier (optional) */}
          <div className="space-y-1.5">
            <Label htmlFor="did-carrier">
              Outbound carrier <span className="text-muted-foreground text-xs">(optional)</span>
            </Label>
            <Select
              value={carrierId || '__auto__'}
              onValueChange={(v) => setCarrierId(v === '__auto__' ? '' : v)}
            >
              <SelectTrigger id="did-carrier">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__auto__">Auto (priority-based)</SelectItem>
                {voiceCarriers.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                    <span className="ms-1.5 text-xs text-muted-foreground">({c.provider})</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Override which carrier is used for outbound calls from this DID.
            </p>
          </div>

          {create.isError && !(create.error as { code?: string })?.code?.includes('did_already_exists') && (
            <p role="alert" className="text-xs text-red-500">
              {create.error instanceof Error ? create.error.message : 'Failed to add DID.'}
            </p>
          )}

          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline" disabled={create.isPending}>
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit" disabled={create.isPending}>
              {create.isPending ? 'Adding…' : 'Add DID'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
